import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

const ROOT_URL: string = '/api/ai-chat/';

export type AppContext = {
	plan_id: number;
	planogram_id: number;
}

export type ChatAvailability = {
	client_access: boolean;
	privacy_policy_accepted: boolean;
	role_access: boolean;
	limit_info?: {
		overused: boolean;
		limit: number;
		used: number;
	}
}

export type ChatList = {
	count: number;
	next: string;
	previous: string;
	results: Array<Chat>;
}

export type Chat = {
	id: number;
	title: string;
	wait_answer: boolean;
}

export type MessageList = {
	count: number;
	next: string;
	previous: string;
	results: Array<AIChatMessage>;
	wait_answer: boolean;
}

export type AIChatMessage = {
	author: string;
	content: {
		role: string;
		parts: Array<{ text: string }>;
	};
	created_at: string;
	id: number;
	liked: boolean;
}

export type LimitInfo = {
	limit: number;
	overused: boolean;
	used: number;
}

@Injectable({
	providedIn: 'root'
})
export class AIAssistantService {

	public activeChatId: number = null;
	public activeCurrentPageButton: boolean = false;
	public activeMessage: string = '';
	public clientAccess: boolean = false;
	public limitInfo: LimitInfo = null;
	public privacyPolicyAccepted: boolean = false;
	public roleAccess: boolean = false;

	constructor(private http: HttpClient) {}

	public leaveRequest(): Observable<{ status: boolean }> {
		return this.http.post<{ status: boolean }>(`api/wizora/access-request/`, {});
	}

	public getAvailability(): Observable<ChatAvailability> {
		return this.http.get<ChatAvailability>(`${ROOT_URL}chat-availability/`);
	}

	public confirmPrivacyPolicy(): Observable<{ status: boolean }> {
		return this.http.post<{ status: boolean }>(`${ROOT_URL}chat-availability/`, { privacy_policy_accepted: true });
	}

	public getChatList(): Observable<ChatList> {
		return this.http.get<ChatList>(`${ROOT_URL}chats/`);
	}

	public changeReaction(chat: number, message: number, reaction: boolean): Observable<{ liked: boolean }> {
		return this.http.patch<{ liked: boolean }>(`${ROOT_URL}chats/${chat}/messages/${message}/`, { liked: reaction });
	}

	public createChat(message: string): Observable<Chat> {
		return this.http.post<Chat>(`${ROOT_URL}chats/`, { first_message: message });
	}

	public getChat(id: number): Observable<Chat> {
		return this.http.get<Chat>(`${ROOT_URL}chats/${id}/`);
	}

	public deleteChat(id: number): Observable<void> {
		return this.http.delete<void>(`${ROOT_URL}chats/${id}/`);
	}

	public getMessages(chat: number): Observable<MessageList> {
		const request: HttpRequest<any> = new HttpRequest<any>(
			'GET',
			`${ROOT_URL}chats/${chat}/messages/`,
			{ responseType: 'text' }
		);

		return new Observable<MessageList>(observer => {
			this.http.request(request).subscribe({
				next: (event: HttpEvent<string>) => {
					if (event.type == HttpEventType.Response) {
						observer.next(JSON.parse(event.body));
						observer.complete();
					}
				},
				error: (err) => observer.error(err),
			});
		});
	}

	public sendMessage(chat: number, message: string, context: Partial<AppContext>): Observable<any> {
		const request: HttpRequest<any> = new HttpRequest<any>(
			'POST',
			`${ROOT_URL}chats/${chat}/messages/`,
			{ message: message, app_context: context },
			{
				responseType: 'text',
				reportProgress: true
			}
		);

		return new Observable<any>(observer => {
			this.http.request(request).subscribe({
				next: (event: HttpEvent<string>) => {
					switch (event.type) {
						case HttpEventType.DownloadProgress:
							const chunk = (event as any).partialText;
							const messages = chunk.split('\n\n').filter((message: string) => message);

							if (messages.length > 1) {
								const response = { parts: [{ text: '' }], role: 'model', id: null, partial: true };
								messages.forEach((message: string) => {
									if (!response.partial) response.parts[0].text = '';
									const converted: any = JSON.parse(message.replace(/^data:\s*/, ''));
									if (converted?.parts?.length) {
										if (converted.partial) {
											response.parts[0].text += converted.parts[0].text;
											response.partial = true;
										} else {
											response.parts[0].text = converted.parts[0].text;
											response.partial = false;
											response.id = converted.id;
										}
									} else if (converted?.error || converted?.overloaded || converted?.limit_error) {
										observer.next(converted);
									}
								});
								observer.next(response);
							} else {
								observer.next(JSON.parse(messages[0].replace(/^data:\s*/, '')));
							}
							break;
						case HttpEventType.Response:
							observer.complete();
							break;
					}
				},
				error: (err) => observer.error(err),
			});
		});
	}
}
